import { SortDirection } from './ColumnMenu';

// A table sorts by one column at a time, so the active sort is a single column key
// paired with a direction rather than a per-column flag.
export interface TableSort<TColumnKey extends string> {
  columnKey: TColumnKey;
  direction: SortDirection;
}

export type TableSortValues<TColumnKey extends string, TRecord> = Record<
  TColumnKey,
  (record: TRecord) => string | number
>;

/**
 * Sorts a copy of `data` by the column named in `sort`, using `sortValues` to turn each
 * record into something comparable. Numbers compare numerically, everything else by
 * locale so mixed-case text sorts as one sequence.
 */
export const sortTableData = <TColumnKey extends string, TRecord>(
  data: TRecord[] | undefined,
  sort: TableSort<TColumnKey>,
  sortValues: TableSortValues<TColumnKey, TRecord>
) => {
  const getSortValue = sortValues[sort.columnKey];

  return data?.slice().sort((a, b) => {
    const aValue = getSortValue(a);
    const bValue = getSortValue(b);

    const comparison =
      typeof aValue === 'number' && typeof bValue === 'number'
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue));

    return sort.direction === 'ascending' ? comparison : -comparison;
  });
};
